import { Component, computed, effect, inject, input, OnInit, signal, viewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { isEqual, sortBy } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, switchMap } from 'rxjs';

import { ActivitiesWithState } from '@models/action';
import { EITransactionInstanceModel } from '@models/module/entityInstance';
import { ActivityDetailsModel } from '@module-instance/activity/common';
import { DocumentDetailsStore } from '@module-instance/dynamic-module/document-details/common';
import { ModuleInstanceService } from '@module-instance/services';
import { CipoStateComponent } from '@shared/components/cipo-state/state.component';
import { CipoImgBase64 } from '@shared/components/img-base64/img-base64.component';
import { UndoTransitionDialog } from '@shared/dialogs';
import {
  AutocompleteDialogComponent,
  AutocompleteDialogData,
} from '@shared/dialogs/autocomplete-dialog/autocomplete-dialog.component';
import { CipoFormModule } from '@shared/modules';
import { ItvDateTimePipe } from '@shared/pipes';
import { NotificationService, UserService } from '@shared/services';
import { DictionaryStore } from '@signal-store/dictionary.store';
import { makeKey } from '@signal-store/services/dictionary.service';
import { ActivityItemContentComponent } from './activity-item-content/activity-item-content.component';

@Component({
  selector: 'cipo-activity-tab',
  imports: [
    MatExpansionModule,
    CipoFormModule,
    CipoImgBase64,
    ItvDateTimePipe,
    CipoStateComponent,
    ActivityItemContentComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './activity-tab.component.html',
  styleUrl: './activity-tab.component.scss',
})
export class ActivityTabComponent implements OnInit {
  readonly ACTIVITIES_WITH_STATE = ActivitiesWithState;
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private notify = inject(NotificationService);
  private moduleInstanceService = inject(ModuleInstanceService);
  private dictionaryStore = inject(DictionaryStore);
  private router = inject(Router);
  private dmStore = inject(DocumentDetailsStore);
  expansionPanels = viewChildren<MatExpansionPanel>('expansionPanel');

  entityInstanceId = input<number>();
  currentDocument = computed(() => this.dmStore.documents()[this.entityInstanceId()]);

  activityInstancesDict = computed(() => {
    return this.currentDocument().activityInstances.reduce(
      (acc, curr) => {
        acc[curr.instance.actionInstanceId] = curr;
        return acc;
      },
      {} as Record<number, { instance: EITransactionInstanceModel; activityDetails?: ActivityDetailsModel }>,
    );
  });
  moduleRoles = computed(() => {
    const { contractId, moduleId } = this.currentDocument().params;
    const key = makeKey({ contractId, moduleId });
    return this.dictionaryStore.moduleRoles()[key]?.data ?? [];
  });

  allExpanded = signal(false);
  expandedActivityId = computed(() => this.currentDocument().params?.activityInstanceId);

  constructor() {
    /* If there is an expanded activity onLoad, scroll to it */
    effect(() => {
      const expansionPanels = this.expansionPanels();
      const expandedActivityId = this.expandedActivityId();
      if (!expansionPanels?.length || !expandedActivityId) {
        return;
      }
      const expandedPanel = expansionPanels.find(panel => panel.expanded);
      ((expandedPanel as any)._elementRef.nativeElement as HTMLElement).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  ngOnInit() {
    const { contractId, moduleId } = this.currentDocument().params;
    /* Load module roles once */
    this.dictionaryStore.getModuleRoles({ contractId, moduleId }).subscribe();
  }

  checkAllExpanded() {
    const allExpanded = this.expansionPanels().every(panel => panel.expanded);
    this.allExpanded.set(allExpanded);
  }

  loadActivityData(actionInstanceId: number, force = false) {
    this.router.navigate([], {
      queryParams: { activity: actionInstanceId },
      queryParamsHandling: 'merge',
    });
    if (this.activityInstancesDict()[actionInstanceId]?.activityDetails && !force) {
      return;
    }
    const { entityInstanceId, moduleId, contractId } = this.currentDocument().params;
    this.dmStore.loadActivityDetails({
      actionInstanceId,
      entityInstanceId,
      moduleId,
      contractId,
      noFields: false,
    });
  }

  toggleAll() {
    const expanded = this.allExpanded();
    if (expanded) {
      this.expansionPanels().forEach(panel => panel.close());
    } else {
      this.expansionPanels().forEach(panel => panel.open());
    }
    this.allExpanded.set(!expanded);
  }

  onPanelClose() {
    this.router.navigate([], {
      queryParams: { activity: null },
      queryParamsHandling: 'merge',
    });
  }

  undoLastAction(activity: ActivityDetailsModel) {
    const { entityInstanceId, actionInstanceId, stateId } = activity;
    const { contractId, moduleId } = this.currentDocument().params;

    this.dialog
      .open(UndoTransitionDialog, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'transition.undoLastAction',
          message: this.translate.instant('transition.rollbackMessage', {
            transition: activity.transitionName,
            user: activity.user,
          }),
          moduleId: moduleId,
          stateId: stateId,
          entityInstanceId: entityInstanceId,
          contractId: contractId,
        },
      })
      .afterClosed()
      .pipe(filter(result => !!result?.confirmed))
      .subscribe(result => {
        this.dmStore.rollbackLastTransition({
          entityInstanceId,
          actionInstanceId,
          notifyUsers: result.notifyUsers,
        });
        this.dmStore.setDocumentAsDirty(entityInstanceId);
      });
  }

  updateNotified(activity: ActivityDetailsModel) {
    const { moduleId, contractId } = this.currentDocument().params;
    this.dialog
      .open<AutocompleteDialogComponent, AutocompleteDialogData>(AutocompleteDialogComponent, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'activity.excludeRoles',
          label: 'activity.selectRoles',
          value: activity.roleToExcludeIds,
          options: this.moduleRoles(),
        },
      })
      .afterClosed()
      .pipe(
        filter(selectedUsers => selectedUsers && !isEqual(sortBy(selectedUsers), sortBy(activity.roleToExcludeIds))),
        switchMap(selectedUsers =>
          this.moduleInstanceService.syncExcludedRoles({
            actionInstanceId: activity.actionInstanceId,
            contractId,
            moduleId,
            body: selectedUsers,
          }),
        ),
      )
      .subscribe(() => {
        this.notify.success('messages.excludedRolesUpdated');
        // TBD: We could just update the activity details in the store, to avoid an unnecessary reload,
        this.loadActivityData(activity.actionInstanceId, true);
      });
  }
}
